/**
 * Industry filter for the master checklist template — Iter 42.
 *
 * Pure functions (no React) so they can be unit-tested with Node.
 *
 * Contract (per user spec):
 *   - Section / general_data_field is visible iff `applies_to_industries` is
 *     falsy / empty (universal) OR includes the client's industry (case-
 *     sensitive exact match — same rule the backend uses for scoring in
 *     `routes/inspections.py`, minus the alias fallback which lives on rubros,
 *     not on checklist template tags).
 *   - Fallback: when there is no industry (null / empty / 'Otro'), the filter
 *     is bypassed and the full template is returned — never hide content
 *     because of missing data.
 *   - Snapshot decision (see `shouldFilter`): the filter is applied only for
 *     "fresh" audits (no answers, no submitted status). Audits already in
 *     progress or submitted always see everything — we never remove content
 *     mid-flow, even if the admin re-tags the master template later.
 */

const FALLBACK_INDUSTRIES = new Set(['', 'Otro', 'otro', 'OTRO']);

/**
 * Should the industry filter be applied at all?
 *
 * @param {Object} args
 * @param {Object|null} args.inspection — payload from GET /certifications/:id
 * @param {Object|null} args.responses  — payload from GET /inspections/:id/responses
 * @param {string|null} args.submittedStatus — 'in_review' | 'approved' | 'rejected' | null
 */
export function shouldFilter({ inspection, responses, submittedStatus }) {
    if (!inspection) return false;
    if (submittedStatus) return false; // post-submit: never filter (view stable)
    const industry = getClientIndustry(inspection);
    if (!industry) return false;
    if (FALLBACK_INDUSTRIES.has(industry)) return false;
    // Detect a fresh audit: no answers and no general_data set.
    const answers = (responses && responses.answers) || {};
    const generalData = (responses && responses.general_data) || {};
    const hasAnswers = Object.keys(answers).length > 0;
    const hasGeneral = Object.values(generalData).some(
        (v) => v !== undefined && v !== null && v !== ''
    );
    return !hasAnswers && !hasGeneral;
}

export function getClientIndustry(inspection) {
    if (!inspection) return '';
    // Priority: explicit request.industry (set at request time) → client.industry (profile)
    return (
        (inspection.request && inspection.request.industry) ||
        (inspection.client && inspection.client.industry) ||
        ''
    );
}

/**
 * Return `template` filtered to only universal + matching-industry
 * sections/general_data_fields. If `industry` is falsy, returns the template
 * verbatim.
 */
export function filterTemplateByIndustry(template, industry) {
    if (!template) return template;
    if (!industry || FALLBACK_INDUSTRIES.has(industry)) return template;

    const keep = (list) =>
        !Array.isArray(list) || list.length === 0 || list.includes(industry);

    return {
        ...template,
        sections: (template.sections || []).filter((s) =>
            keep(s.applies_to_industries)
        ),
        general_data_fields: (template.general_data_fields || []).filter((f) =>
            keep(f.applies_to_industries)
        ),
    };
}
